// src/App.tsx
import React, { useState, useEffect, useRef } from 'react';
import './App.css';
import Header from './components/Header';
import TutorialsPage from './components/TutorialsPage';
import WaitingRoom from './components/WaitingRoom';
import PartnerMatchedPage from './components/PartnerMatchedPage';
import TutorialRound from './components/TutorialRound';
import PracticeRoundIntro from './components/PracticeRoundIntro';
import PostTutorialFeedback from './components/PostTutorialFeedback';
import Game from './components/Game';
import DemographicsPage from './components/DemographicsPage';
import ProlificThankYouPage from './components/ProlificThankYouPage';
import IncompleteThankYouPage from './components/IncompleteThankYouPage';
import DisconnectionNoticePage from './components/DisconnectionNoticePage';
import GameCompletionPage from './components/GameCompletionPage';
import PostGameQuestionsPage from './components/PostGameQuestionsPage';
import PostGameOtherOpinionPage from './components/PostGameOtherOpinionPage';
import PostGameRiskPage from './components/PostGameRiskPage';
import { BrowserRouter as Router, Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { usePageTracking, usePhaseTracking } from './hooks/usePageTracking';
import { getParticipantIdFromSearchParams } from './utils/urlParams';
import { recordStudyStart } from './utils/studyTimingApi';

// Define API base URL at the top
const API_BASE_URL = (process.env.REACT_APP_API_BASE_URL || '').trim();

/** Set to true to skip the tutorial round and the post-tutorial feedback question; go straight to the game after partner matched. */
function envBool(name: string, fallback: boolean): boolean {
  const raw = process.env[name];
  if (raw == null || String(raw).trim() === '') return fallback;
  const s = String(raw).trim().toLowerCase();
  if (['1', 'true', 'yes', 'on'].includes(s)) return true;
  if (['0', 'false', 'no', 'off'].includes(s)) return false;
  return fallback;
}
const SKIP_TUTORIAL_ROUND_AND_FEEDBACK = envBool(
  'REACT_APP_SKIP_TUTORIAL_ROUND_AND_FEEDBACK',
  false
);

type AppPhase = 'INITIALIZING' | 'TUTORIAL' | 'SETUP_ERROR' | 'WAITING_ROOM' | 'PARTNER_MATCHED' | 'PRACTICE_ROUND_INTRO' | 'TUTORIAL_ROUND' | 'POST_TUTORIAL_FEEDBACK' | 'GAME' | 'GAME_OVER';
const ROUND_DURATION = 20;

// Component that handles the main app logic with routing
function AppContent() {
  const navigate = useNavigate();
  const location = useLocation();
  const [currentAppPhase, setCurrentAppPhase] = useState<AppPhase>('INITIALIZING');
  const [currentUserProlificID, setCurrentUserProlificID] = useState<string | null>(null);
  const [setupError, setSetupError] = useState<string | null>(null);
  const [isPerformingSetup, setIsPerformingSetup] = useState<boolean>(false);

  const [currentRound, setCurrentRound] = useState<number>(1);
  const [secondsLeftInRound, setSecondsLeftInRound] = useState<number>(ROUND_DURATION);
  const roundCountdownIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const [isProcessingRoundEnd, setIsProcessingRoundEnd] = useState<boolean>(false);
  const [roundEndMessage, setRoundEndMessage] = useState<string | null>(null);

  const [playerTotalEarnings, setPlayerTotalEarnings] = useState<number>(0);
  const [playerLastRoundEarning, setPlayerLastRoundEarning] = useState<number | null>(null);
  const [partnerLastRoundEarning, setPartnerLastRoundEarning] = useState<number | null>(null);

  const studyStartSentRef = useRef(false);

  useEffect(() => {
    if (!currentUserProlificID || studyStartSentRef.current) return;
    studyStartSentRef.current = true;
    recordStudyStart(currentUserProlificID);
  }, [currentUserProlificID]);

  // After disconnect, browser `online` fires — re-assert start anchor (idempotent) and refresh Player.studyStartedAt merge on server.
  useEffect(() => {
    if (!currentUserProlificID) return;
    const onOnline = () => {
      recordStudyStart(currentUserProlificID);
    };
    window.addEventListener('online', onOnline);
    return () => window.removeEventListener('online', onOnline);
  }, [currentUserProlificID]);

  useEffect(() => {
    console.log("App.tsx: URL parsing effect triggered");
    console.log("App.tsx: Current location:", location);
    console.log("App.tsx: Current URL search params:", location.search);

    // Support both Connect (participantId, assignmentId, projectId) and Prolific URL params
    const participantId = getParticipantIdFromSearchParams(location.search);
    console.log("App.tsx: Extracted participant ID from URL:", participantId ? "(present)" : "(none)");

    if (participantId) {
      setCurrentUserProlificID(participantId);
    } else {
      console.log("App.tsx: No participant ID found in URL. Available query parameters:", Array.from(new URLSearchParams(location.search).keys()));
    }

    // Always allow tutorial phase, even if participant ID is missing
    setCurrentAppPhase('TUTORIAL');
  }, [location.search]);

  useEffect(() => {
    console.log("App.tsx: currentAppPhase changed to:", currentAppPhase);
  }, [currentAppPhase]);

  // Track page based on app phase (for AppContent pages)
  // Note: Tutorial pages are tracked within TutorialsPage component with step details
  // For tutorial phase, we skip tracking here since TutorialsPage handles it with step info
  usePhaseTracking(currentAppPhase, currentUserProlificID, currentAppPhase === 'TUTORIAL' ? { skip: true } : undefined);

  const performPlayerSetup = async (prolificId: string) => {
    console.log("App.tsx: performPlayerSetup started for ID:", prolificId, "isPerformingSetup:", isPerformingSetup);
    if (isPerformingSetup) {
      console.log("App.tsx: performPlayerSetup - already in progress, exiting.");
      return;
    }
    setIsPerformingSetup(true);
    setSetupError(null);
    try {
      console.log("App.tsx: performPlayerSetup - making POST /setup call");
      console.log("App.tsx: API_BASE_URL:", API_BASE_URL);
      console.log("App.tsx: Full setup URL:", `${API_BASE_URL}/setup`);
      const response = await fetch(`${API_BASE_URL}/setup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'prolific-id': prolificId },
      });
      console.log('App.tsx: performPlayerSetup - /setup response status:', response.status);
      const responseData = await response.json();
      console.log('App.tsx: performPlayerSetup - /setup response data:', responseData);

      if (!response.ok) {
        if (response.status === 400 && responseData.error && responseData.error.includes("Prolific ID already exists")) {
          console.log("App.tsx: performPlayerSetup - Player exists. Setting phase to WAITING_ROOM.");
          setCurrentAppPhase('WAITING_ROOM');
        } else {
          // Safely convert error to string, handling cases where it might be an object
          const errorText = responseData.error || responseData.message;
          const errorString = typeof errorText === 'string' ? errorText : JSON.stringify(errorText);
          console.error("App.tsx: performPlayerSetup - /setup error from response:", errorString);
          throw new Error(errorString || `Setup failed with status: ${response.status}`);
        }
      } else {
        console.log("App.tsx: performPlayerSetup - Setup OK. Setting phase to WAITING_ROOM.");
        setCurrentAppPhase('WAITING_ROOM');
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Setup failed";
      console.error("App.tsx: performPlayerSetup - CATCH block error:", errorMessage);
      setSetupError(errorMessage);
      setCurrentAppPhase('SETUP_ERROR');
    } finally {
      console.log("App.tsx: performPlayerSetup - finally block. Setting isPerformingSetup to false.");
      setIsPerformingSetup(false);
    }
  };

  const handleTutorialFinish = () => {
    console.log("App.tsx: handleTutorialFinish CALLED.");
    if (!currentUserProlificID) {
      const errorMsg = "App.tsx: Cannot continue - Participant ID is NOT available after tutorial. Ensure ?participantId=YOUR_ID or ?prolificID=YOUR_ID is in the URL.";
      console.error(errorMsg);
      setSetupError(errorMsg);
      setCurrentAppPhase('SETUP_ERROR');
      return;
    }
    if (SKIP_TUTORIAL_ROUND_AND_FEEDBACK) {
      console.log("App.tsx: Skipping practice round, calling performPlayerSetup.");
      performPlayerSetup(currentUserProlificID);
      return;
    }
    console.log("App.tsx: Tutorial complete, transitioning to PRACTICE_ROUND_INTRO");
    setCurrentAppPhase('PRACTICE_ROUND_INTRO');
  };

  const handleGameReady = () => {
    console.log("App.tsx: Game is ready, transitioning to PARTNER_MATCHED phase");
    setCurrentAppPhase('PARTNER_MATCHED');
  };

  const handleGameStart = () => {
    if (SKIP_TUTORIAL_ROUND_AND_FEEDBACK) {
      console.log("App.tsx: Skip tutorial round and feedback - initializing game and transitioning to GAME phase");
      if (!currentUserProlificID) {
        setCurrentAppPhase('GAME');
        return;
      }
      fetch(`${API_BASE_URL}/postTutorialFeedback/skipAndInitGameRound`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'prolific-id': currentUserProlificID,
        },
      })
        .then((res) => {
          if (!res.ok) {
            return res.json().then((err) => Promise.reject(err));
          }
          return res.json();
        })
        .then((data) => {
          console.log("App.tsx: Game round initialized (skip tutorial):", data);
          setCurrentAppPhase('GAME');
        })
        .catch((err) => {
          console.error("App.tsx: Error initializing game round when skipping tutorial:", err);
          setCurrentAppPhase('GAME');
        });
      return;
    }
    console.log("App.tsx: Partner matched countdown complete, initializing game and transitioning to GAME");
    if (!currentUserProlificID) {
      setCurrentAppPhase('GAME');
      return;
    }
    fetch(`${API_BASE_URL}/postTutorialFeedback/skipAndInitGameRound`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'prolific-id': currentUserProlificID,
      },
    })
      .then((res) => {
        if (!res.ok) {
          return res.json().then((err) => Promise.reject(err));
        }
        return res.json();
      })
      .then((data) => {
        console.log("App.tsx: Game round initialized after partner matched:", data);
        setCurrentAppPhase('GAME');
      })
      .catch((err) => {
        console.error("App.tsx: Error initializing game round after partner matched:", err);
        setCurrentAppPhase('GAME');
      });
  };

  const handlePracticeIntroComplete = () => {
    console.log("App.tsx: Practice intro complete, transitioning to TUTORIAL_ROUND phase");
    setCurrentAppPhase('TUTORIAL_ROUND');
  };

  const handleTutorialRoundComplete = () => {
    console.log("App.tsx: Practice round complete, entering waiting room for partner match");
    if (!currentUserProlificID) {
      setSetupError("Error: User ID missing after practice round.");
      setCurrentAppPhase('SETUP_ERROR');
      return;
    }
    performPlayerSetup(currentUserProlificID);
  };

  const handlePostTutorialFeedbackComplete = () => {
    console.log("App.tsx: Post-tutorial feedback complete, transitioning to GAME phase");
    setCurrentAppPhase('GAME');
  };

  let pageContent;
  switch (currentAppPhase) {
    case 'INITIALIZING':
      pageContent = <div style={{ textAlign: 'center', padding: '50px', fontSize: '18px' }}>Initializing... Please ensure ?participantId=YOUR_ID or ?prolificID=YOUR_ID is in the URL.</div>;
      break;
    case 'TUTORIAL':
      pageContent = <TutorialsPage onTutorialComplete={handleTutorialFinish} />;
      break;
    case 'PRACTICE_ROUND_INTRO':
      pageContent = (
        <PracticeRoundIntro onContinue={handlePracticeIntroComplete} />
      );
      break;
    case 'TUTORIAL_ROUND':
      if (currentUserProlificID) {
        pageContent = <TutorialRound prolificId={currentUserProlificID} onComplete={handleTutorialRoundComplete} />;
      } else {
        setSetupError("Error: User ID missing for Tutorial Round. Please refresh with ?participantId=YOUR_ID or ?prolificID=YOUR_ID.");
        setCurrentAppPhase('SETUP_ERROR');
        pageContent = null;
      }
      break;
    case 'WAITING_ROOM':
      console.log("App.tsx: WAITING_ROOM case - currentUserProlificID:", currentUserProlificID);
      if (currentUserProlificID) {
        console.log("App.tsx: Creating WaitingRoom component with prolificId:", currentUserProlificID);
        pageContent = <WaitingRoom prolificId={currentUserProlificID} onGameReady={handleGameReady} />;
      } else {
        console.log("App.tsx: No prolificID available, setting setup error");
        const currentUrl = window.location.href;
        const exampleUrl = currentUrl.split('?')[0] + '?participantId=YOUR_ID';
        setSetupError(`Error: User ID missing for Waiting Room. Please refresh with ?participantId=YOUR_ID or ?prolificID=YOUR_ID. Current URL: ${currentUrl}. Example: ${exampleUrl}`);
        setCurrentAppPhase('SETUP_ERROR');
        pageContent = null;
      }
      break;
    case 'PARTNER_MATCHED':
      if (currentUserProlificID) {
        pageContent = <PartnerMatchedPage onGameStart={handleGameStart} prolificId={currentUserProlificID} />;
      } else {
        setSetupError("Error: User ID missing for Partner Matched Page. Please refresh with ?participantId=YOUR_ID or ?prolificID=YOUR_ID.");
        setCurrentAppPhase('SETUP_ERROR');
        pageContent = null;
      }
      break;
    case 'POST_TUTORIAL_FEEDBACK':
      if (currentUserProlificID) {
        pageContent = <PostTutorialFeedback prolificId={currentUserProlificID} onComplete={handlePostTutorialFeedbackComplete} />;
      } else {
        setSetupError("Error: User ID missing for Post-Tutorial Feedback. Please refresh with ?participantId=YOUR_ID or ?prolificID=YOUR_ID.");
        setCurrentAppPhase('SETUP_ERROR');
        pageContent = null;
      }
      break;
    case 'GAME':
      if (currentUserProlificID) {
        pageContent = (
          <Game currentUserProlificID={currentUserProlificID} />
        );
      } else {
        setSetupError("Error: User ID missing for Game. Please refresh with ?participantId=YOUR_ID or ?prolificID=YOUR_ID.");
        setCurrentAppPhase('SETUP_ERROR');
        pageContent = null;
      }
      break;
    case 'GAME_OVER':
        pageContent = (
            <div style={{textAlign: 'center', padding: '50px'}}>
                <h2>Game Over!</h2>
                <p>Thank you for playing.</p>
                <p>Your total earnings: {playerTotalEarnings}</p>
                {playerLastRoundEarning !== null && <p>Your earnings from the last round played: {playerLastRoundEarning}</p>}
            </div>
        );
        break;
    case 'SETUP_ERROR':
      const currentUrl = window.location.href;
      const baseUrl = currentUrl.split('?')[0];
      const testUrl = `${baseUrl}?participantId=test123`;
      
      pageContent = (
        <div style={{ padding: '20px', textAlign: 'center', border: '1px solid red', margin: '20px', borderRadius: '8px' }}>
          <h2>Application Error</h2>
          <p style={{ color: 'red' }}>{setupError || "An unexpected error occurred."}</p>
          <div style={{ margin: '20px 0', padding: '20px', backgroundColor: '#f5f5f5', borderRadius: '5px' }}>
            <h3>How to fix this:</h3>
            <p>Add <code>?participantId=YOUR_ID</code> or <code>?prolificID=YOUR_ID</code> to the URL</p>
            <p><strong>Current URL:</strong> {currentUrl}</p>
            <p><strong>Correct format:</strong> {testUrl}</p>
            <p><strong>Example:</strong> Replace "test123" with your actual Prolific ID</p>
          </div>
          <button onClick={() => window.location.reload()}>Refresh Page</button>
        </div>
      );
      break;
    default:
      pageContent = <div>Loading Application...</div>;
  }

  return (
    <div className="App">
      {isPerformingSetup && <div style={{textAlign: 'center', padding: '20px', fontSize: '18px', color: '#333'}}>Setting up your game session, please wait...</div>}
      {currentAppPhase === 'GAME' && roundEndMessage && !isProcessingRoundEnd && (
          <div style={{textAlign: 'center', padding: '10px', margin: '0 10px', borderRadius: '5px', backgroundColor: roundEndMessage.includes('Error') ? '#ffebee' : '#e8f5e9', color: roundEndMessage.includes('Error') ? '#c62828' : '#2e7d32' }}>
              {roundEndMessage}
          </div>
      )}
      {!isPerformingSetup && pageContent}
    </div>
  );
}

// Component to handle disconnection redirects and render content
function AppWithRedirect() {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Get participant ID from URL (Connect participantId or Prolific ID) for page tracking
  const prolificID = getParticipantIdFromSearchParams(location.search);

  // Track pages based on route changes (for route-based pages like /demographics, /post-game-questions, etc.)
  // Note: This only tracks route-based pages. Phase-based pages (in AppContent) are tracked separately.
  usePageTracking(prolificID);
  
  // Check disconnection status on mount and poll regularly
  useEffect(() => {
    const checkDisconnectionStatus = async () => {
      const queryParams = new URLSearchParams(location.search);
      const prolificID = getParticipantIdFromSearchParams(location.search);
      const wasDisconnected = queryParams.get('wasDisconnected');
      
      console.log("AppWithRedirect: Checking disconnection status - prolificID:", prolificID, "wasDisconnected:", wasDisconnected, "current path:", location.pathname);
      
      // If we're on demographics, post-game questions, or game completion page, don't do any redirects
      if (location.pathname === '/demographics' || location.pathname === '/post-game-questions' || location.pathname === '/post-game-other-opinion' || location.pathname === '/post-game-risk' || location.pathname === '/session-finalized' || location.pathname === '/disconnection-notice') {
        console.log("AppWithRedirect: On demographics, post-game questions, risk questionnaire, game completion, or disconnection notice page, skipping disconnection checks");
        return;
      }
      
      // If URL already has wasDisconnected flag, redirect immediately (but not if we're already on disconnection notice)
      if (wasDisconnected === '1' && location.pathname !== '/disconnection-notice') {
        console.log("AppWithRedirect: wasDisconnected detected in URL, redirecting to disconnection notice");
        console.log("AppWithRedirect: Current pathname:", location.pathname);
        navigate('/disconnection-notice' + location.search, { replace: true });
        return;
      } else if (wasDisconnected === '1') {
        console.log("AppWithRedirect: wasDisconnected detected but not redirecting because we're on:", location.pathname);
      }
      
      // If we have a prolificID, check with server if this participant was disconnected
      if (prolificID) {
        try {
          console.log("AppWithRedirect: Checking disconnection status for prolificID:", prolificID);
          const res = await fetch(`${API_BASE_URL}/isGroupDisconnected`, {
            headers: { 'prolific-id': prolificID }
          });
          const data = await res.json();
          
          console.log("AppWithRedirect: Server response:", data);
          
          // CRITICAL: If game is completed, NEVER redirect to disconnection notice
          if (data.gameStatus === 'completed') {
            console.log("AppWithRedirect: Game is completed - skipping disconnection check");
            return;
          }
          
          if (data.isDisconnected) {
            console.log("AppWithRedirect: Server confirmed disconnection, redirecting to disconnection notice");
            // Add wasDisconnected flag to URL and redirect
            const newSearch = new URLSearchParams(location.search);
            newSearch.set('wasDisconnected', '1');
            navigate('/disconnection-notice?' + newSearch.toString(), { replace: true });
            return;
          }
        } catch (error) {
          console.error("AppWithRedirect: Error checking disconnection status:", error);
        }
      }
    };
    
    // Run immediately
    checkDisconnectionStatus();
    
    // Set up polling every 4 seconds (same as Game component) - but only if not on demographics, post-game questions, or game completion
    if (location.pathname !== '/demographics' && location.pathname !== '/post-game-questions' && location.pathname !== '/post-game-other-opinion' && location.pathname !== '/post-game-risk' && location.pathname !== '/session-finalized') {
      const interval = setInterval(checkDisconnectionStatus, 4000);
      
      // Cleanup interval on unmount
      return () => clearInterval(interval);
    }
  }, [navigate, location.search, location.pathname]); // Include pathname in dependencies


  
  return (
    <Routes>
      <Route path="/disconnection-notice" element={<DisconnectionNoticePage />} />
      <Route path="/session-finalized" element={<GameCompletionPage />} />
      <Route path="/post-game-questions" element={<PostGameQuestionsPage />} />
      <Route path="/post-game-other-opinion" element={<PostGameOtherOpinionPage />} />
      <Route path="/post-game-risk" element={<PostGameRiskPage />} />
      <Route path="/demographics" element={<DemographicsPage />} />
      <Route path="/prolific-thank-you" element={<ProlificThankYouPage />} />
      <Route path="/participation-acknowledged" element={<IncompleteThankYouPage />} />
      <Route path="*" element={<AppContent />} />
    </Routes>
  );
}

function App() {
  return (
    <Router>
      <AppWithRedirect />
    </Router>
  );
}

export default App;

